import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import { OnboardingTooltip } from './OnboardingTooltip';
import { useOnboarding } from '../../hooks/useOnboarding';

// Guide steps for the knowledge onboarding
type GuideStep = 
  | 'create_collection'
  | 'fill_collection_title'
  | 'add_content'
  | 'select_subject'
  | 'add_title'
  | 'drop_file'
  | 'wait_indexing'
  | 'complete';

interface KnowledgeOnboardingGuideProps {
  uploadPanelOpen: boolean;
}

export function KnowledgeOnboardingGuide({
  uploadPanelOpen,
}: KnowledgeOnboardingGuideProps) {
  const navigate = useNavigate();
  const { progress, completeStep, fetchProgress } = useOnboarding();
  const [currentStep, setCurrentStep] = useState<GuideStep | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [guideEnabled, setGuideEnabled] = useState(false);
  
  // Track session-based progress (independent of actual data)
  const [collectionCreatedThisSession, setCollectionCreatedThisSession] = useState(false);
  const [uploadPanelWasOpen, setUploadPanelWasOpen] = useState(false);
  const [contentAddedThisSession, setContentAddedThisSession] = useState(false);
  
  // Skip initial minimize to allow widget to stay open after section completion
  const isInitialMount = useRef(true);
  // Skip minimize right after completing a step (widget should stay expanded)
  const justCompletedStep = useRef(false);

  // Refresh progress when component mounts
  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // Guide activation gate: only show tooltips when user explicitly started the guide
  useEffect(() => {
    const initial = localStorage.getItem('onboarding:guide-active') === 'true';
    setGuideEnabled(initial);

    const handleActivated = () => setGuideEnabled(true);
    const handleDeactivated = () => setGuideEnabled(false);
    window.addEventListener('onboarding:guide-activated', handleActivated);
    window.addEventListener('onboarding:guide-deactivated', handleDeactivated);
    return () => {
      window.removeEventListener('onboarding:guide-activated', handleActivated);
      window.removeEventListener('onboarding:guide-deactivated', handleDeactivated);
    };
  }, []);

  // Listen for reset events - reset all tracking states
  useEffect(() => {
    const handleReset = () => {
      setDismissed(false);
      setCollectionCreatedThisSession(false);
      setUploadPanelWasOpen(false);
      setContentAddedThisSession(false);
    };
    
    window.addEventListener('onboarding:reset', handleReset);
    return () => {
      window.removeEventListener('onboarding:reset', handleReset);
    };
  }, []);

  // Check if we should show the knowledge onboarding guide
  // Show if: first_knowledge is not completed AND onboarding is active (not completed/skipped)
  const isOnboardingActive = progress?.status === 'in_progress' || progress?.status === 'not_started';
  const isKnowledgeStepPending = !progress?.steps_completed?.includes('first_knowledge');
  const isOnboardingKnowledgeStep = guideEnabled && isOnboardingActive && isKnowledgeStepPending;

  // Track when upload panel opens
  useEffect(() => {
    if (uploadPanelOpen && isOnboardingKnowledgeStep) {
      setUploadPanelWasOpen(true);
    }
  }, [uploadPanelOpen, isOnboardingKnowledgeStep]);

  // Detect when content is added (upload panel was open, now closed)
  useEffect(() => {
    if (!isOnboardingKnowledgeStep) return;
    
    // If upload panel was open and is now closed, content was likely added
    if (uploadPanelWasOpen && !uploadPanelOpen) {
      setContentAddedThisSession(true);
    }
  }, [uploadPanelOpen, uploadPanelWasOpen, isOnboardingKnowledgeStep]);

  // Determine current step based on state
  useEffect(() => {
    if (!isOnboardingKnowledgeStep || dismissed) {
      setCurrentStep(null);
      return;
    }

    // Show completion if content was added during THIS session
    if (contentAddedThisSession) {
      setCurrentStep('complete');
      return;
    }

    // If upload panel is open, guide through upload steps
    if (uploadPanelOpen) {
      // Check what's filled in the form
      const subjectSelect = document.querySelector('[data-onboarding="subject-select"]') as HTMLSelectElement;
      const titleInput = document.querySelector('[data-onboarding="title-input"]') as HTMLInputElement;

      if (!subjectSelect?.value) {
        setCurrentStep('select_subject');
      } else if (!titleInput?.value) {
        setCurrentStep('add_title');
      } else {
        setCurrentStep('drop_file');
      }
      return;
    }

    // Check if collection creation modal is open
    const collectionTitleInput = document.querySelector('[data-onboarding="collection-title-input"]');
    if (collectionTitleInput) {
      setCurrentStep('fill_collection_title');
      return;
    }

    // If no collection created this session, start with create collection
    if (!collectionCreatedThisSession) {
      setCurrentStep('create_collection');
      return;
    }

    // Otherwise, prompt to add content
    setCurrentStep('add_content');
  }, [isOnboardingKnowledgeStep, dismissed, uploadPanelOpen, collectionCreatedThisSession, contentAddedThisSession]);

  // Watch for collection modal opening/closing
  useEffect(() => {
    if (!isOnboardingKnowledgeStep || dismissed) return;

    const checkCollectionModal = () => {
      const collectionTitleInput = document.querySelector('[data-onboarding="collection-title-input"]');
      if (collectionTitleInput && currentStep === 'create_collection') {
        setCurrentStep('fill_collection_title');
      } else if (!collectionTitleInput && currentStep === 'fill_collection_title') {
        // Modal closed, collection was created - move to add_content
        setCollectionCreatedThisSession(true);
        setCurrentStep('add_content');
      }
    };

    const observer = new MutationObserver(checkCollectionModal);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isOnboardingKnowledgeStep, dismissed, currentStep]);

  // Listen for form changes in upload panel
  useEffect(() => {
    if (!uploadPanelOpen || !isOnboardingKnowledgeStep) return;

    const checkFormState = () => {
      const subjectSelect = document.querySelector('[data-onboarding="subject-select"]') as HTMLSelectElement;
      const titleInput = document.querySelector('[data-onboarding="title-input"]') as HTMLInputElement;

      if (subjectSelect?.value && currentStep === 'select_subject') {
        setCurrentStep('add_title');
      }
      if (titleInput?.value && currentStep === 'add_title') {
        setCurrentStep('drop_file');
      }
    };

    const interval = setInterval(checkFormState, 500);
    return () => clearInterval(interval);
  }, [uploadPanelOpen, isOnboardingKnowledgeStep, currentStep]);

  // Notify widget when guide has active tooltip (to hide widget's own tooltip)
  useEffect(() => {
    if (currentStep && currentStep !== 'complete') {
      window.dispatchEvent(new CustomEvent('onboarding:guide-active'));
    } else {
      window.dispatchEvent(new CustomEvent('onboarding:guide-inactive'));
    }
  }, [currentStep]);

  // Auto-minimize widget when actively doing steps (skip on initial mount or after step completion)
  useEffect(() => {
    if (!currentStep) return;
    
    // Skip minimize on initial mount to let widget stay open after section completion
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Skip minimize right after completing a step
    if (justCompletedStep.current) {
      justCompletedStep.current = false;
      return;
    }
    
    const activeSteps: GuideStep[] = [
      'create_collection', 'fill_collection_title', 
      'add_content', 'select_subject', 'add_title', 'drop_file', 'wait_indexing'
    ];
    
    if (activeSteps.includes(currentStep)) {
      window.dispatchEvent(new CustomEvent('onboarding:widget-minimize'));
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    setDismissed(true);
  }, []);

  const handleComplete = useCallback(async () => {
    justCompletedStep.current = true;
    await completeStep('first_knowledge');
    window.dispatchEvent(new CustomEvent('onboarding:step-completed'));
    window.dispatchEvent(new CustomEvent('onboarding:widget-expand'));
    setCurrentStep(null);
    // Navigate to agents for the next step (first_test)
    navigate('/agents');
  }, [completeStep, navigate]);

  if (!isOnboardingKnowledgeStep || dismissed || !currentStep) {
    return null;
  }

  // Completion modal
  if (currentStep === 'complete') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="relative w-full max-w-md mx-4 bg-zinc-900 rounded-2xl border border-emerald-500/50 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="px-6 py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              Bravo ! Première connaissance ajoutée 🎉
            </h2>
            <p className="text-zinc-300 text-sm mb-6">
              Votre contenu est maintenant indexé et prêt à enrichir vos agents IA.
              Passons à l'étape suivante : tester votre agent !
            </p>
            <button
              onClick={handleComplete}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors"
            >
              Tester mon agent
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tooltip configurations
  const tooltipConfigs: Record<GuideStep, {
    targetSelector: string;
    title: string;
    description: string;
    position: 'top' | 'bottom' | 'left' | 'right';
    showNextButton: boolean;
    nextLabel?: string;
  }> = {
    create_collection: {
      targetSelector: '[data-onboarding="create-collection-btn"], [title="Nouvelle collection"]',
      title: 'Créer une collection',
      description: 'Commencez par créer une collection pour organiser vos contenus. Cliquez sur le bouton + pour ajouter une nouvelle collection.',
      position: 'right',
      showNextButton: false,
    },
    fill_collection_title: {
      targetSelector: '[data-onboarding="collection-title-input"]',
      title: 'Nommer votre collection',
      description: 'Entrez un nom pour votre collection (ex: "Formation IA", "Cours Marketing"), puis cliquez sur "Créer la collection".',
      position: 'left',
      showNextButton: false,
    },
    add_content: {
      targetSelector: '[data-onboarding="add-content-btn"]',
      title: 'Ajouter un contenu',
      description: 'Ajoutez maintenant un document à votre base de connaissances. Cliquez sur "+ Contenu" pour ouvrir le panneau d\'import.',
      position: 'bottom',
      showNextButton: false,
    },
    select_subject: {
      targetSelector: '#upload-subject',
      title: 'Sélectionner un sujet',
      description: 'Choisissez le sujet dans lequel vous souhaitez ranger ce contenu.',
      position: 'left',
      showNextButton: false,
    },
    add_title: {
      targetSelector: '[data-onboarding="title-input"], input[placeholder*="titre"]',
      title: 'Donner un titre',
      description: 'Donnez un titre descriptif à votre contenu pour le retrouver facilement.',
      position: 'left',
      showNextButton: false,
    },
    drop_file: {
      targetSelector: '[data-onboarding="file-zone"], [class*="border-dashed"]',
      title: 'Importer un fichier',
      description: 'Glissez-déposez un fichier (PDF, Word, texte, audio, vidéo) ou cliquez pour sélectionner.',
      position: 'left',
      showNextButton: false,
    },
    wait_indexing: {
      targetSelector: 'body',
      title: 'Indexation en cours',
      description: 'Votre contenu est en cours de traitement. Patientez quelques instants...',
      position: 'bottom',
      showNextButton: false,
    },
    complete: {
      targetSelector: 'body',
      title: '',
      description: '',
      position: 'bottom',
      showNextButton: false,
    },
  };

  const config = tooltipConfigs[currentStep];
  if (!config) return null;

  return (
    <OnboardingTooltip
      targetSelector={config.targetSelector}
      title={config.title}
      description={config.description}
      step={currentStep === 'create_collection' ? 1 : currentStep === 'fill_collection_title' ? 1 : currentStep === 'add_content' ? 2 : currentStep === 'select_subject' ? 3 : currentStep === 'add_title' ? 4 : 5}
      totalSteps={5}
      position={config.position}
      onSkip={handleSkip}
      showNextButton={config.showNextButton}
      nextLabel={config.nextLabel}
      isVisible={true}
    />
  );
}

export default KnowledgeOnboardingGuide;
